import { Theme } from '@mui/material';

import { ValidColorPath } from './button-common.types';

const FALLBACK_COLOR = 'inherit';

// Styled interpolations re-run on every render, so a broken path would warn
// endlessly without this.
const warnedPaths = new Set<string>();

const warnOnce = (message: string) => {
  if (warnedPaths.has(message)) {
    return;
  }
  warnedPaths.add(message);
  console.warn(message);
};

// Keys that would let a path walk out of the palette and into the prototype
// chain.
const UNSAFE_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

// Reads a single own (never inherited) property, so traversal stays inside the
// palette object itself.
const readOwnProperty = (source: object, key: string): unknown =>
  !UNSAFE_KEYS.has(key) && Object.prototype.hasOwnProperty.call(source, key)
    ? Reflect.get(source, key)
    : undefined;

// Resolves a dotted palette path. Callers pass optional-chained variant lookups,
// so the path can be undefined, and a theme may not define every key. This runs
// during render, so an unresolved path falls back to an inherited color rather
// than taking down the tree the button is in.
export const getThemeValue = (theme: Theme, path?: ValidColorPath): string => {
  if (!path) {
    warnOnce(
      `getThemeValue: called without a palette path; falling back to "${FALLBACK_COLOR}".`,
    );
    return FALLBACK_COLOR;
  }

  let resolved: unknown = theme.palette;

  for (const key of path.split('.')) {
    const next =
      resolved && typeof resolved === 'object'
        ? readOwnProperty(resolved, key)
        : undefined;

    if (next === undefined) {
      warnOnce(
        `getThemeValue: "${path}" is not defined in the theme palette; falling back to "${FALLBACK_COLOR}".`,
      );
      return FALLBACK_COLOR;
    }

    resolved = next;
  }

  if (typeof resolved !== 'string') {
    warnOnce(
      `getThemeValue: "${path}" did not resolve to a color; falling back to "${FALLBACK_COLOR}".`,
    );
    return FALLBACK_COLOR;
  }

  return resolved;
};
